<script lang="ts">
  import StepActionCTA from "../../../../../../../step-action-cta"
  import {
    appointmentMeetsStartBound,
    PLANNING_START_BOUND_MESSAGE,
    type AppointmentPlanningStartBound,
  } from "../../../../../../_shared/_logic/start-bound"
  import StatusTag from "@kontextwerk/web-sdk/ui/display/StatusTag.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import TableFormCell from "@kontextwerk/web-sdk/ui/layout/TableFormCell.svelte"
  import { RICH_TOOLTIP_CLASS } from "../../../../../../../../../../../_shared/_logic/rich-tooltips"
  import type {
    AppointmentPlanningManualAppointmentPayload,
    AppointmentPlanningManualFlexSlotOption,
  } from "../../../../../../../../../../../api/kehrblatt/planning"
  import type {
    PlanningPreviewRow,
    PlanningPreviewRowChange,
  } from "../../../../../../_shared/_logic/table-contract"
  import {
    dateOnlyFromAppointmentPlanningInputValue,
    dateTimeFromAppointmentPlanningInputValue,
    readAppointmentPlanningInputEventValue,
    type AppointmentPlanningInputEventValue,
  } from "../../../../../../_shared/_logic/input-events"
  import { manualFlexSlotValue } from "./_logic/manualFlexSlotSelection"

  type Props = {
    startBound: AppointmentPlanningStartBound
    row: PlanningPreviewRow
    disabled?: boolean
    onChange: (row: PlanningPreviewRow, change: PlanningPreviewRowChange) => void
  }

  const { row, startBound, disabled = false, onChange }: Props = $props()
  let inputError = $state<string | null>(null)
  const localMinimum = $derived(
    Intl.DateTimeFormat().resolvedOptions().timeZone === startBound.timezone
      ? `${startBound.startDate}T00:00`
      : undefined,
  )

  const pad = (value: number): string => String(value).padStart(2, "0")

  const toLocalDateTimeInput = (value?: string | null): string => {
    if (!value) return ""

    const parsed = new Date(value)

    if (!Number.isFinite(parsed.getTime())) return ""

    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`
  }

  const durationMinutes = (): number => {
    const fromForm = Number.parseInt(String(row.estimatedDurationValue ?? ""), 10)

    if (Number.isFinite(fromForm) && fromForm > 0) return fromForm

    if (row.manualAppointment?.start_at && row.manualAppointment.end_at) {
      const start = new Date(row.manualAppointment.start_at)
      const end = new Date(row.manualAppointment.end_at)
      const diff = Math.round((end.getTime() - start.getTime()) / 60000)

      if (Number.isFinite(diff) && diff > 0) return diff
    }

    if (row.appointmentStartAt && row.appointmentEndAt) {
      const start = new Date(row.appointmentStartAt)
      const end = new Date(row.appointmentEndAt)
      const diff = Math.round((end.getTime() - start.getTime()) / 60000)

      if (Number.isFinite(diff) && diff > 0) return diff
    }

    return 60
  }

  const currentManual = (): AppointmentPlanningManualAppointmentPayload | null =>
    row.manualAppointment ?? null

  const manualWithStart = (
    inputValue: AppointmentPlanningInputEventValue,
  ): AppointmentPlanningManualAppointmentPayload | null | undefined => {
    if (!row.appointmentEmployeeId) return undefined

    const start = dateTimeFromAppointmentPlanningInputValue(inputValue)

    if (start === undefined) return undefined

    if (start === null) return null

    const end = new Date(start.getTime() + durationMinutes() * 60000)
    const flexType = row.appointmentTypeValue === "FLEX" ? "SOFT" : "HARD"

    return {
      ...(currentManual() ?? {}),
      start_at: start.toISOString(),
      end_at: end.toISOString(),
      employee_id: row.appointmentEmployeeId,
      flex_type: flexType,
      timeframe_start_at: flexType === "SOFT" ? start.toISOString() : null,
      timeframe_end_at: flexType === "SOFT" ? end.toISOString() : null,
    }
  }

  const manualFlexOptions = (): AppointmentPlanningManualFlexSlotOption[] =>
    row.manualFlexSlotOptionsState.status === "ready"
      ? row.manualFlexSlotOptionsState.options.filter((slot) =>
          appointmentMeetsStartBound(
            {
              start_at: slot.appointment_start_at,
              flex_type: "SOFT",
              timeframe_start_at: slot.timeframe_start_at,
            },
            startBound,
          ),
        )
      : []

  const selectedManualFlexSlotValue = (): string =>
    manualFlexSlotValue({
      manualFlexSlotId: row.manualFlexSlotId,
      appointmentEmployeeId: row.appointmentEmployeeId,
      manualAppointment: row.manualAppointment,
      appointmentStartAt: row.appointmentStartAt,
      appointmentEndAt: row.appointmentEndAt,
      appointmentTimeframeStartAt: row.appointmentTimeframeStartAt,
      appointmentTimeframeEndAt: row.appointmentTimeframeEndAt,
      options: manualFlexOptions(),
    })

  const slotOptionsForSelectedDay = () => {
    if (!row.appointmentEmployeeId) return [{ label: "Terminmitarbeiter wählen", value: "" }]

    if (row.manualFlexSlotOptionsState.status === "loading") {
      return [{ label: "Flextermine werden geladen...", value: "" }]
    }

    if (row.manualFlexSlotOptionsState.status === "error") {
      return [
        {
          label:
            row.manualFlexSlotOptionsState.message || "Flextermine konnten nicht geladen werden",
          value: "",
        },
      ]
    }

    if (row.manualFlexSlotOptionsState.status === "idle") {
      return [{ label: "Flextermine werden geladen...", value: "" }]
    }

    const options = manualFlexOptions().map((slot) => ({
      label: slot.label,
      value: slot.slot_id,
    }))

    return options.length ? options : [{ label: "Keine Flextermine", value: "" }]
  }

  const slotSelectDisabled = (): boolean =>
    disabled ||
    !row.appointmentEmployeeId ||
    row.manualFlexSlotOptionsState.status === "loading" ||
    row.manualFlexSlotOptionsState.status === "error"

  const handleManualDateTime = (event: Event) => {
    const input = readAppointmentPlanningInputEventValue(event)

    if (!input.found) return

    const manualAppointment = manualWithStart(input.value)

    if (manualAppointment === undefined) return

    if (!appointmentMeetsStartBound(manualAppointment, startBound)) {
      inputError = PLANNING_START_BOUND_MESSAGE

      return
    }

    inputError = null
    onChange(row, { kind: "manual_appointment", value: manualAppointment })
  }

  const handleManualFlexDate = (event: Event) => {
    const input = readAppointmentPlanningInputEventValue(event)

    if (!input.found) return

    const value = dateOnlyFromAppointmentPlanningInputValue(input.value)

    if (value === undefined) return

    if (value && value < startBound.startDate) {
      inputError = PLANNING_START_BOUND_MESSAGE

      return
    }

    inputError = null
    onChange(row, { kind: "manual_flex_date", value })
  }

  const handleManualSlot = (event: Event) => {
    const detail = (event as CustomEvent<{ value?: unknown }>).detail
    const value =
      typeof detail?.value === "string"
        ? detail.value
        : (event.target as HTMLInputElement | null)?.value
    const slotId = String(value ?? "").trim()

    if (!slotId) {
      if (selectedManualFlexSlotValue()) onChange(row, { kind: "manual_flex_slot", value: null })

      return
    }

    const selected = manualFlexOptions().find((option) => option.slot_id === slotId)

    if (!selected) return

    onChange(row, { kind: "manual_flex_slot", value: selected })
  }
</script>

<div class="appointment-cell">
  {#if row.planningStrategy === "MANUAL" && row.appointmentTypeValue === "HARD"}
    <div class="hard-appointment-input">
      <TableFormCell>
        <Input
          type="datetime-local"
          min={localMinimum}
          placeholder="Termin"
          labelVisibility="hidden"
          value={toLocalDateTimeInput(row.manualAppointment?.start_at ?? row.appointmentStartAt)}
          brightBackground={true}
          surfaceTone="light"
          classList="appointment-planning-table-input"
          disabled={disabled || !row.appointmentEmployeeId}
          onChange={handleManualDateTime}
        />
      </TableFormCell>
    </div>
  {:else if row.planningStrategy === "MANUAL" && row.appointmentTypeValue === "FLEX"}
    <div class="manual-flex-inputs">
      <div class="manual-flex-date">
        <TableFormCell>
          <Input
            type="date"
            min={startBound.startDate}
            placeholder="Datum"
            labelVisibility="hidden"
            value={row.manualFlexDate}
            brightBackground={true}
            surfaceTone="light"
            classList="appointment-planning-table-input"
            {disabled}
            onChange={handleManualFlexDate}
          />
        </TableFormCell>
      </div>
      <div class="manual-flex-slot">
        <TableFormCell>
          <Input
            type="select"
            placeholder="Slot"
            labelVisibility="hidden"
            options={slotOptionsForSelectedDay()}
            value={selectedManualFlexSlotValue()}
            brightBackground={true}
            surfaceTone="light"
            classList="appointment-planning-table-select appointment-planning-flex-slot-select"
            selectStyle="width: 100%; min-width: 0; max-width: 100%;"
            fitSelectedText={true}
            disabled={slotSelectDisabled()}
            onChange={handleManualSlot}
          />
        </TableFormCell>
      </div>
    </div>
  {:else}
    <StatusTag
      size="sm"
      label={row.appointmentTag.label}
      subLabel={row.appointmentTag.subLabel ?? undefined}
      color={row.appointmentTag.color}
      icon={row.appointmentTag.icon ?? undefined}
      tooltipContent={row.appointmentTag.tooltip ?? ""}
      tooltipHtml={row.appointmentTag.tooltipHtml ?? false}
      tooltipClassName={RICH_TOOLTIP_CLASS}
      disableTitle={true}
    />
  {/if}
  {#if row.planningStrategy === "MANUAL" && row.appointmentTypeValue === "FLEX" && row.manualFlexSlotOptionsState.status === "error"}
    <StepActionCTA
      type="tertiaryButton"
      content="Erneut versuchen"
      small={true}
      {disabled}
      onClick={() => onChange(row, { kind: "retry_manual_flex_slots" })}
    />
  {/if}
  {#if inputError}<span class="input-error" role="alert">{inputError}</span>{/if}
</div>

<style lang="less">
  .input-error {
    color: var(--error-200, #b91c1c);
    font-size: 0.8rem;
  }
  .appointment-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  .manual-flex-inputs {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.45rem;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
  }

  .hard-appointment-input,
  .manual-flex-date,
  .manual-flex-slot {
    min-width: 0;
    max-width: 100%;
  }

  .hard-appointment-input {
    width: min(100%, 15rem);
  }

  .manual-flex-date {
    flex: 0 1 8.5rem;
  }

  .manual-flex-slot {
    flex: 1 1 12rem;
    overflow: hidden;
  }

  .hard-appointment-input :global(.kw-table-form-cell),
  .manual-flex-date :global(.kw-table-form-cell),
  .manual-flex-slot :global(.kw-table-form-cell) {
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  .manual-flex-slot :global(.select-input-shell),
  .manual-flex-slot :global(.svelte-select) {
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  .manual-flex-slot :global(.select-input-shell[data-fit-selected-text="true"]),
  .manual-flex-slot :global(.select-fit-sizer) {
    width: 100%;
    max-width: 100%;
  }
</style>
